/**
 * Omnibox input: pill container with attach and send/stop controls,
 * plus a separate voice-mode button. ⌘K focuses the input. Controlled
 * internally (local text/attachment state only) — the store owns send/abort
 * via props.
 *
 * Modality parity with `ConversationInput`: attachments via the shared
 * composer-attach helpers (pick, paste, drop; 5-file cap and per-file
 * errors), voice via the same speech-mode toggle + `AudioRecorderContext`.
 * In voice mode the pill swaps its content for the waveform + controls; the
 * actual audio send happens through `AudioSender` mounted by `StageVoice`.
 */
import {
  type ChangeEvent,
  type ClipboardEvent,
  type DragEvent,
  type FC,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { observer } from 'mobx-react-lite';
import { cn } from '@/app/lib/utils';
import { Mic, MicOff, Phone, X } from 'lucide-react';
import { useGlobalAudioPlayer } from 'react-use-audio-player';
import { useIntl, type IntlShape } from 'react-intl';
import type { Attachment } from '@/app/lib/types/files';
import { useMessagingStore } from '@/app/lib/hooks/useMessagingStore';
import { useAudioRecorderContext } from '@/app/lib/contexts';
import { AttachmentChip } from '@/app/lib/components/input/AttachmentChip';
import { Button } from '@/app/lib/shadcdn/button';
import { AdaptiveWaveform } from '@/app/lib/components/AgentInput/AdaptiveWaveform';
import {
  type AttachError,
  COMPOSER_ACCEPT_ATTR,
  COMPOSER_MAX_FILES,
  filesToAttachments,
} from '@/app/lib/files/composer-attach';
import { messages } from '@/app/lib/localization/messages.ts';
import { useComposerDraft } from './ComposerDraftContext.tsx';
import { composerItemText, type ComposerItem } from './composer-draft.ts';
import { useVoiceToggle } from '../hooks/useVoiceToggle.ts';

export interface StageOmniboxProps {
  placeholder: string;
  pending: boolean;
  ready: boolean;
  /** Voice-mode line (live caption or status word) rendered inside the
   *  waveform area — the dock's guidance slot is hidden during voice. */
  voiceLine?: string | null;
  /** True when the line is a status word — renders with the shimmer. */
  voiceLineStatus?: boolean;
  onSend: (text: string, files?: Attachment[], selections?: ComposerItem[]) => void;
  onStop: () => void;
  onStartVoice?: () => void;
}

function attachErrorText(err: AttachError, intl: IntlShape): string {
  if (err.code === 'too_large') {
    return intl.formatMessage(messages.attachmentTooLarge, { filename: err.filename });
  }
  if (err.code === 'unsupported') {
    return intl.formatMessage(messages.attachmentUnsupported, {
      filename: err.filename,
      extension: err.extension,
    });
  }
  return intl.formatMessage(messages.attachmentLimit, { count: err.allowed });
}

export const StageOmnibox: FC<StageOmniboxProps> = observer(
  ({ placeholder, pending, ready, onSend, onStop, voiceLine, voiceLineStatus, onStartVoice }) => {
    const [text, setText] = useState('');
    const composerDraft = useComposerDraft();
    const [attachments, setAttachments] = useState<Attachment[]>([]);
    const [errors, setErrors] = useState<AttachError[]>([]);
    const [isDragOver, setIsDragOver] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);
    // Mirror for synchronous reads inside the async ingest path — overlapping
    // drops must not both observe the same pre-ingest count.
    const attachmentsRef = useRef<Attachment[]>([]);
    const intl = useIntl();

    const messagesStore = useMessagingStore();
    const toggleVoice = useVoiceToggle();
    const {
      enableMicrophone,
      disableMicrophone,
      isRecording,
      isPending,
      startRecording,
      stopRecording,
    } = useAudioRecorderContext();
    const { playing: isAISpeaking, pause } = useGlobalAudioPlayer();

    useEffect(() => {
      const handleKeyDown = (event: globalThis.KeyboardEvent) => {
        const isFocusShortcut = (event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k';
        if (!isFocusShortcut) {
          return;
        }
        event.preventDefault();
        inputRef.current?.focus();
      };
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const ingestFiles = useCallback(async (incoming: File[]) => {
      if (incoming.length === 0) {
        return;
      }
      setErrors([]);
      const { attachments: parsed, errors: errs } = await filesToAttachments(
        incoming,
        attachmentsRef.current,
      );
      if (parsed.length > 0) {
        setAttachments((prev) => {
          const next = [...prev, ...parsed].slice(0, COMPOSER_MAX_FILES);
          attachmentsRef.current = next;
          return next;
        });
      }
      if (errs.length > 0) {
        setErrors(errs);
      }
    }, []);

    const handlePickFiles = (event: ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files ? Array.from(event.target.files) : [];
      void ingestFiles(files);
      event.target.value = '';
    };

    const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
      const items = event.clipboardData.items;
      if (!items || items.length === 0) {
        return;
      }
      const files: File[] = [];
      for (const item of items) {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          if (file) {
            files.push(file);
          }
        }
      }
      if (files.length > 0) {
        event.preventDefault();
        void ingestFiles(files);
      }
    };

    const handleDrop = (event: DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      setIsDragOver(false);
      const files = event.dataTransfer.files ? Array.from(event.dataTransfer.files) : [];
      void ingestFiles(files);
    };

    const removeAttachment = (index: number) => {
      setAttachments((prev) => {
        const next = prev.filter((_, i) => i !== index);
        attachmentsRef.current = next;
        return next;
      });
      setErrors([]);
    };

    // The placeholder is an overlay span, not the native attribute:
    // `field-sizing: content` sizes an empty textarea to its placeholder, so a
    // placeholder longer than one line wraps and stretches the whole pill on
    // narrow viewports. The span truncates to one line instead.
    const showPlaceholder = text.length === 0;

    const hasDraft =
      text.trim().length > 0 || attachments.length > 0 || !!composerDraft?.items.length;
    const canSend = ready && hasDraft;

    const handleSend = () => {
      if (!canSend) {
        return;
      }
      onSend(text, attachments.length > 0 ? attachments : undefined, composerDraft?.items);
      setText('');
      setAttachments([]);
      attachmentsRef.current = [];
      setErrors([]);
      if (window.matchMedia('(pointer: coarse)').matches) {
        inputRef.current?.blur();
      }
    };

    const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
      }
    };

    const handleVoiceToggle = () => {
      if (onStartVoice && !messagesStore.speechEnabled) {
        onStartVoice();
        return;
      }
      toggleVoice();
    };

    // Stopping sends the clip through the recorder pipeline.
    const handleRecordToggle = () => {
      if (isRecording || isPending) {
        messagesStore.toggleRecordingAudio(false);
        stopRecording();
        return;
      }
      if (isAISpeaking) {
        pause();
      }
      messagesStore.toggleRecordingAudio(true);
      startRecording();
    };

    const handleVoiceExit = () => {
      if (isRecording || isPending) {
        messagesStore.toggleRecordingAudio(false);
        stopRecording();
      }
      handleVoiceToggle();
    };

    // Voice mode: ambient, one idiom for both engines — full-width waveform
    // with call-sized controls docked bottom-right. The waveform carries
    // the channel state. Realtime: mute + end. v0: record-toggle + end
    // (press mic to record, press again to stop-and-send).
    if (messagesStore.speechEnabled) {
      const realtime = messagesStore.voiceEngine === 'realtime';
      const recordingHot = isRecording || isPending;
      return (
        <div data-dock-backdrop-edge className="stage-voice w-full relative isolate min-h-28 pt-12">
          <div className="absolute inset-x-0 top-0 h-28 px-6 pointer-events-none">
            <AdaptiveWaveform
              isActive={messagesStore.isRecordingAudio || isAISpeaking}
              isAISpeaking={isAISpeaking}
              isWorking={
                pending ||
                messagesStore.voiceRunBusy ||
                messagesStore.voiceState === 'thinking' ||
                messagesStore.voiceState === 'building' ||
                messagesStore.voiceState === 'connecting'
              }
            />
          </div>
          <div className="relative flex items-center gap-3 px-2 sm:px-8">
            <div className="pointer-events-none min-w-0 flex-1 pl-4 sm:pl-0" aria-live="polite">
              <span
                className={cn(
                  'line-clamp-2 break-words text-sm font-medium text-foreground',
                  voiceLineStatus && 'shimmer-text',
                )}
              >
                {voiceLine}
              </span>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {realtime ? (
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  className="stage-voice-control pointer-events-auto rounded-full border-border bg-muted text-muted-foreground shadow-none hover:bg-muted hover:text-foreground data-[active=true]:text-warning"
                  data-active={messagesStore.voiceMuted}
                  aria-pressed={messagesStore.voiceMuted}
                  onClick={() => messagesStore.setVoiceMuted(!messagesStore.voiceMuted)}
                  aria-label={intl.formatMessage(
                    messagesStore.voiceMuted ? messages.unmuteMicrophone : messages.muteMicrophone,
                  )}
                >
                  {messagesStore.voiceMuted ? <MicOff size={24} /> : <Mic size={24} />}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="stage-voice-control pointer-events-auto rounded-full border-border bg-muted px-4 text-sm font-semibold text-muted-foreground shadow-none hover:bg-muted hover:text-foreground data-[active=true]:border-primary data-[active=true]:bg-primary data-[active=true]:text-primary-foreground data-[active=true]:hover:bg-primary data-[active=true]:hover:text-primary-foreground"
                  data-active={recordingHot}
                  onClick={handleRecordToggle}
                >
                  <Mic size={18} className="mr-1.5" />
                  {intl.formatMessage(recordingHot ? messages.tapToSend : messages.tapToSpeak)}
                </Button>
              )}
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="stage-voice-control pointer-events-auto rounded-full shadow-lift"
                onClick={realtime ? handleVoiceToggle : handleVoiceExit}
                aria-label={intl.formatMessage(messages.endVoice)}
              >
                <Phone size={24} className="rotate-135" aria-hidden="true" />
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div
        className="stage-omnibox group"
        data-dragover={isDragOver}
        data-has-draft={hasDraft}
        onDragOver={(event) => {
          event.preventDefault();
          setIsDragOver(true);
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          setIsDragOver(false);
        }}
        onDrop={handleDrop}
      >
        <input
          type="file"
          ref={fileInputRef}
          multiple
          accept={COMPOSER_ACCEPT_ATTR}
          onChange={handlePickFiles}
          className="hidden"
        />
        {attachments.length > 0 && (
          <div className="pointer-events-auto flex flex-wrap items-center gap-2 px-1.5 pb-2">
            {attachments.map((attachment, index) => {
              const key = `${attachment.name}-${index}-${attachment.updateTms}`;
              if (attachment.type.startsWith('image/')) {
                return (
                  <div key={key} className="relative">
                    <img
                      src={`data:${attachment.type};base64,${attachment.data}`}
                      alt={attachment.name}
                      title={attachment.name}
                      className="h-14 w-14 rounded-md border border-border object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => removeAttachment(index)}
                      aria-label={intl.formatMessage(messages.removeAttachment, {
                        filename: attachment.name,
                      })}
                      className="absolute -right-1.5 -top-1.5 grid h-5 w-5 cursor-pointer place-items-center rounded-full border border-border bg-card text-muted-foreground shadow-elevated hover:text-foreground"
                    >
                      <X size={11} />
                    </button>
                  </div>
                );
              }
              return (
                <AttachmentChip
                  key={key}
                  name={attachment.name}
                  mediaType={attachment.type}
                  sizeBytes={Math.floor((attachment.data.length * 3) / 4)}
                  onRemove={() => removeAttachment(index)}
                />
              );
            })}
          </div>
        )}
        {errors.length > 0 && (
          <div className="mb-2 w-fit rounded-xl border border-border bg-card px-3 py-2 text-xs text-warning">
            {errors.map((err, index) => (
              <div key={`${err.code}-${index}`}>{attachErrorText(err, intl)}</div>
            ))}
          </div>
        )}
        <div data-dock-backdrop-edge className="flex items-end gap-2.5">
          <div
            className="stage-composer pointer-events-auto relative flex min-w-0 flex-1 flex-col overflow-hidden rounded-composer border border-border bg-card p-2.5 shadow-lift focus-within:border-ring/50 group-data-[dragover=true]:border-dashed group-data-[dragover=true]:border-primary"
            data-pending={pending}
          >
            {!!composerDraft?.items.length && (
              <div className="max-h-40 w-full overflow-y-auto px-3 pb-2 pt-1" aria-live="polite">
                {composerDraft.items.map((item) => (
                  <div
                    key={`${item.surfaceId}:${item.id}`}
                    className="flex items-start gap-2 py-1 text-sm leading-6 text-foreground"
                  >
                    <p className="min-w-0 flex-1 whitespace-pre-wrap break-words">
                      {composerItemText(item)}
                    </p>
                    <button
                      type="button"
                      onClick={() => composerDraft.remove(item)}
                      aria-label={intl.formatMessage(messages.removeFromMessage, {
                        label: item.label,
                      })}
                      className="shrink-0 rounded p-1 text-muted-foreground hover:text-foreground focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary"
                    >
                      <X size={14} aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            <div className="flex w-full items-end gap-2">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-11 w-11 flex-none rounded-full"
                onClick={() => fileInputRef.current?.click()}
                aria-label={intl.formatMessage(messages.attachFiles)}
              >
                <svg
                  className="h-4.5 w-4.5"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  aria-hidden="true"
                >
                  <path d="M12 5v14" />
                  <path d="M5 12h14" />
                </svg>
              </Button>
              <div className="stage-composer-copy relative flex min-h-11 min-w-0 flex-1 items-center me-3 sm:me-0">
                <textarea
                  ref={inputRef}
                  rows={1}
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  onKeyDown={handleKeyDown}
                  onPaste={handlePaste}
                  className="stage-noscroll field-sizing-content max-h-[8lh] min-w-0 flex-1 resize-none border-none bg-transparent py-2.5 text-base leading-6 text-foreground outline-none"
                  aria-label={placeholder}
                />
                {showPlaceholder && (
                  <span
                    className="pointer-events-none absolute inset-x-0 top-1/2 block -translate-y-1/2 overflow-hidden text-ellipsis whitespace-nowrap text-base leading-6 text-muted-foreground"
                    aria-hidden="true"
                    title={placeholder}
                  >
                    {placeholder}
                  </span>
                )}
              </div>
              {pending ? (
                <Button
                  type="button"
                  size="icon"
                  className="h-11 w-11 flex-none rounded-full"
                  onClick={onStop}
                  aria-label={intl.formatMessage(messages.stopGenerating)}
                >
                  <svg className="h-4.5 w-4.5" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="4" y="4" width="16" height="16" rx="3" fill="currentColor" />
                  </svg>
                </Button>
              ) : null}
              {!pending || hasDraft ? (
                <Button
                  type="button"
                  size="icon"
                  variant="composer"
                  className="h-11 w-11 flex-none rounded-full"
                  onClick={handleSend}
                  disabled={!canSend}
                  aria-label={intl.formatMessage(messages.send)}
                >
                  <svg
                    className="h-4.5 w-4.5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="1.8"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="m5 12 7-7 7 7" />
                    <path d="M12 19V5" />
                  </svg>
                </Button>
              ) : null}
            </div>
          </div>
          <Button
            type="button"
            variant="composer"
            className="stage-call-button pointer-events-auto relative flex-none self-center overflow-hidden rounded-full p-0 text-sm font-semibold shadow-sm active:scale-95 motion-reduce:scale-100"
            onClick={handleVoiceToggle}
            disabled={!ready}
            aria-label={intl.formatMessage(messages.speak)}
            title={intl.formatMessage(messages.voiceMode)}
          >
            <Phone size={20} strokeWidth={1.8} aria-hidden="true" />
            <span className="stage-call-label" aria-hidden="true">
              {intl.formatMessage(messages.speak)}
            </span>
          </Button>
        </div>
      </div>
    );
  },
);
